/* BOGO pack builder (currently "Buy 1 Get 1 Free" — one 4-pack paid, one free).
 *
 * Derived from echo-bundle.js (the Packs builder), kept separate on purpose so the
 * live Packs page is unaffected by changes here.
 *
 * FLAVOR COUNT AND CART QUANTITY ARE SEPARATE NUMBERS
 * --------------------------------------------------
 * This product is itself a 4-pack, so one unit already means four jars. The customer
 * picks 8 flavors (4 paid + 4 free) but the cart holds 2 UNITS — one paid pack and
 * one free pack — which is what a Buy 1 Get 1 automatic discount triggers on.
 *
 *   data-paid-count / data-free-count  -> flavors picked per pack (4 and 4)
 *   data-line-qty                      -> units each pack adds to the cart (1)
 *
 * Conflating the two is how an earlier version added 8 units: 8 four-packs (32 jars)
 * charged at 4 x pack price, instead of 2 packs (8 jars) charged at 1 x pack price.
 *
 * WHY THIS DOESN'T USE THE THEME'S NORMAL FORM SUBMIT
 * ---------------------------------------------------
 * Line item properties live on the line item, and Shopify's automatic discount
 * splits a discounted line into two rows by cloning it — properties and all. That
 * made checkout list all 8 flavors twice, and checkout is not themeable, so no
 * amount of Liquid could fix it.
 *
 * So the two packs are added as TWO line items, each carrying only its own four
 * flavors (Flavor 1-4 and Flavor 5-8). Because their properties differ, Shopify
 * keeps them as separate lines and never merges them. That needs a single
 * /cart/add.js call with an `items` array, which the theme's form submit can't
 * express — hence the capture-phase interception below.
 *
 * Flavors are numbered 1..N straight through rather than "Flavor" vs "Free Flavor".
 * Shopify chooses which line the discount lands on, so a line labelled "free" could
 * end up being the charged one; continuous numbering can never contradict it.
 *
 * Other differences from echo-bundle.js:
 *   - No variant gating. The product is single-variant ("Default Title"), so
 *     getPackSize() / active_variants / shapes:product:variantchange all drop out
 *     and the builder is simply always active.
 *   - Two independent slot groups: the flavors for the paid pack AND for the free
 *     pack, which may be different.
 *   - Slots hold a fixed position. Removing a paid pick blanks that one slot rather
 *     than re-indexing, so it can't pull a free pick up into the paid group.
 *   - Express payment buttons are hidden: Shop Pay / PayPal post the form directly
 *     and cannot express two line items.
 *   - Both lines are tagged properties[_b4g4] so cart-items.liquid can lock the
 *     quantity and remove the pair together. Underscore prefix keeps it hidden from
 *     customer display.
 */
(function () {
  const builder = document.querySelector('[data-b4g4-bundle]');
  if (!builder) return;

  /* Scope the form lookup to the product content wrapper. A bare
     document.querySelector('[data-product-form]') would pick up a quick-buy form
     from any product tile that happens to render earlier in the DOM. */
  const productContent = builder.closest('.product-content');
  const productForm =
    (productContent && productContent.querySelector('[data-product-form]')) ||
    document.querySelector('[data-product-form]');
  if (!productForm) return;

  const paidContainer = builder.querySelector('[data-b4g4-slots]');
  const freeContainer = builder.querySelector('[data-b4g4-free-slots]');
  const productCards = builder.querySelectorAll('.vpb__product-card');
  const addToCartBtn = productForm.querySelector("[type='submit']");
  const messageBox = builder.querySelector('.vpb__message');

  /* Flavors picked per pack. */
  const paidCount = parseInt(builder.dataset.paidCount, 10) || 4;
  const freeCount = Math.max(parseInt(builder.dataset.freeCount, 10) || 0, 0);
  const slotCount = paidCount + freeCount;

  /* Units each pack adds to the cart — NOT a flavor count. 1 for a multi-pack
     product, so Buy 1 Get 1 sees 2 units in the cart. */
  const lineQty = Math.max(parseInt(builder.dataset.lineQty, 10) || 1, 1);

  /* Fixed-position slots: index < paidCount is a paid slot, the rest are free. */
  const selected = new Array(slotCount).fill(null);
  let submitting = false;

  function filledCount() {
    return selected.filter(Boolean).length;
  }

  /* -----------------------------------
     Neutralise competing checkout paths
  -----------------------------------*/

  /* Express buttons post the product form straight to Shopify, so they can't create
     the two line items this offer needs. Hide them outright rather than gating them. */
  function hideDynamicButtons() {
    const buttons = [
      productForm.querySelector('.shopify-payment-button'),
      document.querySelector('.shopify-payment-button'),
    ];
    buttons.forEach((el) => {
      if (el) el.style.display = 'none';
    });
  }

  /* The quantity is fixed by the offer, so the b4g4 template shouldn't include the
     quantity block. If it's there anyway, hide it — we never read it. */
  function hideThemeQuantityInput() {
    const inputs = [
      productForm.querySelector('[name="quantity"]'),
      document.querySelector(
        'input[name="quantity"][form="' + productForm.id + '"]'
      ),
    ];
    inputs.forEach((input) => {
      if (!input) return;
      input.disabled = true;
      const wrapper =
        input.closest('data-island') || input.closest('.product-block');
      if (wrapper) wrapper.classList.add('hidden');
    });
  }

  /* -----------------------------------
     Helpers
  -----------------------------------*/

  function showMessage(text) {
    if (!messageBox) return;
    messageBox.textContent = text;
    messageBox.style.display = 'block';

    setTimeout(() => {
      messageBox.style.display = 'none';
    }, 4000);
  }

  function variantId() {
    const input = productForm.querySelector('[name="id"]');
    if (!input) return null;
    return input.options
      ? input.options[input.selectedIndex].value
      : input.value;
  }

  /* One line item per pack. `slots` is how many flavors that pack carries;
     `quantity` is lineQty, independent of the flavor count. Flavors are numbered
     1..N straight through across both packs. */
  function buildCartItems() {
    const id = variantId();
    if (!id) return null;

    const packs = [
      { start: 0, slots: paidCount },
      { start: paidCount, slots: freeCount },
    ].filter((p) => p.slots > 0);

    return packs.map((p) => {
      const properties = { _b4g4: '1' };
      for (let i = p.start; i < p.start + p.slots; i++) {
        properties[`Flavor ${i + 1}`] = selected[i].title;
      }
      return { id: id, quantity: lineQty, properties: properties };
    });
  }

  /* -----------------------------------
     Render Slots
  -----------------------------------*/

  function buildSlot(index, isFree) {
    const slot = document.createElement('div');
    slot.className = 'vpb__slot' + (isFree ? ' vpb__slot--free' : '');

    const pick = selected[index];

    if (pick) {
      slot.innerHTML = `
        <img src="${pick.image}" width="100%" />
        <span class="vpb_product-title hidden">${pick.title}</span>
        <button type="button" class="vpb__remove" aria-label="Remove ${pick.title}">✕</button>
        ${isFree ? '<span class="vpb__free-badge">FREE</span>' : ''}
      `;

      slot.querySelector('.vpb__remove').addEventListener('click', () => {
        selected[index] = null;
        render();
      });
    } else {
      slot.innerHTML = '<span class="vpb_empty rte">Empty</span>';
      if (isFree) {
        slot.innerHTML += '<span class="vpb__free-badge">FREE</span>';
      }
    }

    return slot;
  }

  function render() {
    if (!paidContainer) return;
    paidContainer.innerHTML = '';
    if (freeContainer) freeContainer.innerHTML = '';

    for (let i = 0; i < slotCount; i++) {
      const isFree = i >= paidCount;
      const container = isFree ? freeContainer : paidContainer;
      if (!container) continue;
      container.appendChild(buildSlot(i, isFree));
    }

    hideDynamicButtons();
  }

  /* -----------------------------------
     Add Product Click
  -----------------------------------*/

  /* Clicks fill the paid slots first, then overflow into the free slots. */
  productCards.forEach((card) => {
    card.addEventListener('click', function () {
      const next = selected.indexOf(null);
      if (next === -1) {
        showMessage('You have already selected the maximum products.');
        return;
      }

      selected[next] = {
        title: card.dataset.title,
        image: card.dataset.image,
        variantId: card.dataset.variant,
      };

      render();
    });
  });

  /* -----------------------------------
     Add To Cart
  -----------------------------------*/

  function addToCart() {
    const items = buildCartItems();
    if (!items) {
      showMessage(window.theme?.strings?.cartError || 'Something went wrong.');
      return;
    }

    submitting = true;
    if (addToCartBtn) addToCartBtn.setAttribute('disabled', 'disabled');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          showMessage(
            data.description ||
              data.message ||
              window.theme?.strings?.cartError ||
              'Something went wrong.'
          );
          return;
        }

        /* The free jar widget hooks the form's `submit` in the bubble phase, which
           our capture-phase stopImmediatePropagation() suppresses along with the
           theme's own handler — the two are indistinguishable by phase or element.
           So invoke its add explicitly. Sequenced before the refresh so the drawer
           opens showing the jar too, and swallowed on failure: the packs are
           already in the cart and must not be held hostage to the add-on. */
        return Promise.resolve()
          .then(() => {
            /* The free jar snippet finds its form by product handle while we scope
               to .product-content, so don't assume both resolved to the same node. */
            const host = productForm.__addFreeJarIfOptedIn
              ? productForm
              : Array.prototype.find.call(
                  document.querySelectorAll('[data-product-form]'),
                  (f) => typeof f.__addFreeJarIfOptedIn === 'function'
                );
            return host && host.__addFreeJarIfOptedIn();
          })
          .catch((err) => {
            console.warn('[b4g4] free jar add failed:', err);
          })
          .then(finishAdd);
      })
      .catch((error) => {
        console.error(error);
        showMessage(
          window.theme?.strings?.cartError || 'Something went wrong.'
        );
      })
      .finally(() => {
        submitting = false;
        if (addToCartBtn) addToCartBtn.removeAttribute('disabled');
      });
  }

  /* Refresh the cart sections, then either open the drawer the way modal-cart.js
     does or fall back to the cart page. Calling the Alpine store directly avoids
     faking a shapes:cart:afteradditem payload, whose listeners expect a
     single-line-item response shape. */
  function finishAdd() {
    document.dispatchEvent(new CustomEvent('theme:update:cart'));

    if (window.theme?.settings?.cartType === 'modal') {
      try {
        window.Alpine?.store('modals')?.open('cart');
      } catch (e) {
        /* Drawer is a nicety; the cart itself is already updated. */
      }
    } else {
      window.location.href = '/cart';
    }
  }

  /* Capture phase so this runs before Alpine's own @submit handler, which would
     otherwise post the form and create a single qty-1 line with no flavors. */
  document.addEventListener(
    'submit',
    function (e) {
      if (e.target !== productForm) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (submitting) return;

      const remaining = slotCount - filledCount();
      if (remaining > 0) {
        showMessage(
          `Please select ${remaining} more flavor${remaining > 1 ? 's' : ''}.`
        );
        return;
      }

      addToCart();
    },
    true
  );

  /* -----------------------------------
     On Page Load
  -----------------------------------*/

  hideThemeQuantityInput();
  render();

  /* Shopify injects .shopify-payment-button asynchronously, so it may not exist
     during the first render. */
  window.addEventListener('load', hideDynamicButtons);
})();